/**
 * Inventory API Layer.
 *
 * React Query hooks for asset CRUD, cost entries, statuses, and barcodes.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type { PaginatedResponse } from "@/types/common";
import type {
  Asset,
  AssetCostEntry,
  AssetCostEntryCreate,
  AssetCreate,
  AssetUpdate,
  BarcodeData,
  CustomStatus,
} from "@/types/asset";

// ---------------------------------------------------------------------------
// Assets
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Filtering (FS-029)
// ---------------------------------------------------------------------------

export interface ComputedFilterField {
  key: string;
  label: string;
  /** "number" or "boolean" — decides which operators are offered. */
  kind: string;
  description: string;
}

export interface FilterReference {
  fields: string[];
  computed_fields: ComputedFilterField[];
  operators: string[];
  operators_by_kind: Record<string, string[]>;
  max_depth: number;
  sortable: string[];
  search_fields: string[];
  condition: Record<string, string[]>;
}

/**
 * What the list can be filtered and sorted by, read from the server.
 *
 * The panel has no copy of any of this. A hardcoded list is how the status
 * filter ended up offering thirteen names that no tenant's lifecycle matched.
 */
export function useFilterReference() {
  return useQuery<FilterReference>({
    queryKey: ["assets", "filter-reference"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/assets/filter-reference"))
        .data as FilterReference,
    // The shape of the model, not the data in it.
    staleTime: 1000 * 60 * 30,
  });
}

export interface AssetSearchBody {
  filters?: unknown;
  search?: string;
  statuses?: string[];
  location_id?: string;
  show_archived?: boolean;
  sort_by?: string;
  sort_dir?: "asc" | "desc";
  page?: number;
  page_size?: number;
}

/**
 * Search inventory with a filter tree.
 *
 * A POST because a nested tree is a body, not a query string — and because a
 * URL long enough to hold one gets truncated by something between here and
 * the server.
 */
export function useAssetSearch(body: AssetSearchBody) {
  return useQuery<PaginatedResponse<Asset>>({
    queryKey: ["assets", "search", body],
    queryFn: async () =>
      (await apiClient.post("/api/v1/assets/search", body))
        .data as PaginatedResponse<Asset>,
    placeholderData: (previous) => previous,
  });
}

interface UseAssetsParams {
  page?: number;
  pageSize?: number;
  status?: string;
  category?: string;
  search?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  minPrice?: number;
  maxPrice?: number;
  dateFrom?: string;
  dateTo?: string;
  isOnHold?: boolean;
  showArchived?: boolean;
}

export function useAssets(params: UseAssetsParams = {}) {
  const {
    page = 1,
    pageSize = 25,
    status,
    category,
    search,
    sortBy,
    sortDir,
    minPrice,
    maxPrice,
    dateFrom,
    dateTo,
    isOnHold,
    showArchived,
  } = params;

  return useQuery<PaginatedResponse<Asset>>({
    queryKey: [
      "assets",
      {
        page,
        pageSize,
        status,
        category,
        search,
        sortBy,
        sortDir,
        minPrice,
        maxPrice,
        dateFrom,
        dateTo,
        isOnHold,
        showArchived,
      },
    ],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.set("page", String(page));
      searchParams.set("page_size", String(pageSize));
      if (status) searchParams.set("status", status);
      if (category) searchParams.set("category", category);
      if (search) searchParams.set("search", search);
      if (sortBy) searchParams.set("sort_by", sortBy);
      if (sortDir) searchParams.set("sort_dir", sortDir);
      if (minPrice != null) searchParams.set("min_price", String(minPrice));
      if (maxPrice != null) searchParams.set("max_price", String(maxPrice));
      if (dateFrom) searchParams.set("date_from", dateFrom);
      if (dateTo) searchParams.set("date_to", dateTo);
      if (isOnHold != null) searchParams.set("is_on_hold", String(isOnHold));
      if (showArchived) searchParams.set("show_archived", "true");

      const resp = await apiClient.get(`/api/v1/assets?${searchParams}`);
      return resp.data as PaginatedResponse<Asset>;
    },
  });
}

export function useAsset(assetId: string | undefined) {
  return useQuery<Asset>({
    queryKey: ["assets", assetId],
    queryFn: async () => {
      const resp = await apiClient.get(`/api/v1/assets/${assetId}`);
      return resp.data as Asset;
    },
    enabled: !!assetId,
  });
}

export function useCreateAsset() {
  const queryClient = useQueryClient();

  return useMutation<Asset, Error, AssetCreate>({
    mutationFn: async (data) => {
      const resp = await apiClient.post("/api/v1/assets", data);
      return resp.data as Asset;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assets"] });
    },
  });
}

export function useUpdateAsset(assetId: string) {
  const queryClient = useQueryClient();

  return useMutation<Asset, Error, AssetUpdate>({
    mutationFn: async (data) => {
      const resp = await apiClient.put(`/api/v1/assets/${assetId}`, data);
      return resp.data as Asset;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assets"] });
    },
  });
}

export function useDeleteAsset() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: async (assetId) => {
      await apiClient.delete(`/api/v1/assets/${assetId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assets"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Cost Entries
// ---------------------------------------------------------------------------

export function useAssetCosts(assetId: string | undefined) {
  return useQuery<AssetCostEntry[]>({
    queryKey: ["assets", assetId, "costs"],
    queryFn: async () => {
      const resp = await apiClient.get(`/api/v1/assets/${assetId}/costs`);
      return resp.data as AssetCostEntry[];
    },
    enabled: !!assetId,
  });
}

export function useAddCostEntry(assetId: string) {
  const queryClient = useQueryClient();

  return useMutation<AssetCostEntry, Error, AssetCostEntryCreate>({
    mutationFn: async (data) => {
      const resp = await apiClient.post(
        `/api/v1/assets/${assetId}/costs`,
        data,
      );
      return resp.data as AssetCostEntry;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assets", assetId] });
      queryClient.invalidateQueries({ queryKey: ["assets", assetId, "costs"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Barcodes
// ---------------------------------------------------------------------------

export function useBarcode(assetId: string | undefined) {
  return useQuery<BarcodeData>({
    queryKey: ["assets", assetId, "barcode"],
    queryFn: async () => {
      const resp = await apiClient.get(`/api/v1/assets/${assetId}/barcode`);
      return resp.data as BarcodeData;
    },
    enabled: !!assetId,
  });
}

// ---------------------------------------------------------------------------
// Custom Statuses
// ---------------------------------------------------------------------------

export function useStatuses(entityType: string) {
  return useQuery<CustomStatus[]>({
    queryKey: ["statuses", entityType],
    queryFn: async () => {
      const resp = await apiClient.get(
        `/api/v1/statuses?entity_type=${entityType}`,
      );
      return resp.data as CustomStatus[];
    },
  });
}

// ---------------------------------------------------------------------------
// Asset Work Orders (for cost breakdown and history)
// ---------------------------------------------------------------------------

interface WorkOrderSummary {
  id: string;
  wo_number: string;
  title: string;
  description: string | null;
  work_type: string;
  status: string;
  priority: string;
  total_labor_cost: number;
  total_parts_cost: number;
  total_materials_cost: number;
  total_cost: number;
  created_at: string;
  updated_at: string;
}

export function useAssetWorkOrders(assetId: string | undefined) {
  return useQuery<PaginatedResponse<WorkOrderSummary>>({
    queryKey: ["asset-work-orders", assetId],
    queryFn: async () =>
      (
        await apiClient.get(
          `/api/v1/work-orders?asset_id=${assetId}&page_size=100`,
        )
      ).data as PaginatedResponse<WorkOrderSummary>,
    enabled: !!assetId,
  });
}

export function useCreateWorkOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      (await apiClient.post("/api/v1/work-orders", data)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["asset-work-orders"] });
      qc.invalidateQueries({ queryKey: ["work-orders"] });
    },
  });
}
